import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import ProbabilityOutcomeTable from "../../../../components/ui/ProbabilityOutcomeTable";
import { formatFractionLatex, simplifyFraction } from "../../../../utils/fractionLatex";

const ORIGINAL_VARIANT = { X: [1, 3, 5, 7, 9], Y: [0, 2, 4, 6, 8], div: 6 };
const FALLBACK_VARIANT = { X: [1, 2, 4, 5], Y: [0, 2, 4, 6, 8], div: 4 };
const SET_SIZE_MIN = 3;
const SET_SIZE_MAX = 7;

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

function sampleDigits(pool, size) {
  const shuffled = [...pool];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = randInt(0, index);
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled.slice(0, size).sort((a, b) => a - b);
}

function countFavourable({ X, Y, div }) {
  return X.flatMap((x) => Y.map((y) => 10 * x + y)).filter(
    (value) => value % div === 0
  ).length;
}

export function isSetDimensionAllowed(xSize, ySize) {
  if (
    xSize < SET_SIZE_MIN ||
    xSize > SET_SIZE_MAX ||
    ySize < SET_SIZE_MIN ||
    ySize > SET_SIZE_MAX
  ) {
    return false;
  }
  const smaller = Math.min(xSize, ySize);
  const larger = Math.max(xSize, ySize);
  return !(smaller === 3 && larger <= 4) && !(larger === 7 && smaller >= 6);
}

function pickSetDimensions() {
  const allowed = [];
  for (let xSize = SET_SIZE_MIN; xSize <= SET_SIZE_MAX; xSize += 1) {
    for (let ySize = SET_SIZE_MIN; ySize <= SET_SIZE_MAX; ySize += 1) {
      if (isSetDimensionAllowed(xSize, ySize)) allowed.push([xSize, ySize]);
    }
  }
  return pick(allowed);
}

function createRandomVariant() {
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const [xSize, ySize] = pickSetDimensions();
    const variant = {
      X: sampleDigits([1, 2, 3, 4, 5, 6, 7, 8, 9], xSize),
      Y: sampleDigits([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], ySize),
      div: pick([2, 3, 4, 5, 6]),
    };
    const fav = countFavourable(variant);
    const total = variant.X.length * variant.Y.length;
    if (fav > 0 && fav < total) return variant;
  }
  return FALLBACK_VARIANT;
}

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  const variant = original ? ORIGINAL_VARIANT : createRandomVariant();
  const fav = countFavourable(variant);
  return {
    type: "MaturaTwoDigitSetsDivisibilityProbabilityOpenTask",
    data: { ...variant, fav, total: variant.X.length * variant.Y.length, maxPoints: 2 },
    source: {
      variant: original ? "original" : "random",
      exam: "MATURA",
      session: original ? "2026_MAJ" : undefined,
      taskNumber: "30",
    },
  };
}

function rawValue(value) {
  return String(value?.raw ?? value ?? "").trim();
}

function numberValue(value) {
  const source = rawValue(value).replace(",", ".");
  if (source.includes("/")) {
    const [numerator, denominator] = source.split("/").map(Number);
    return numerator / denominator;
  }
  return Number(source);
}

function matches(value, expected) {
  return rawValue(value) !== "" && Math.abs(numberValue(value) - expected) < 1e-9;
}

function answerLatex(value) {
  const raw = rawValue(value);
  return raw ? formatLatexPreview(raw) : "\\text{--}";
}

function probabilityResult(fav, total) {
  const reduced = simplifyFraction(fav, total);
  return {
    ...reduced,
    latex: formatFractionLatex(reduced.num, reduced.den),
    calculationLatex:
      reduced.num === fav && reduced.den === total
        ? `\\frac{${fav}}{${total}}`
        : `\\frac{${fav}}{${total}}=${formatFractionLatex(reduced.num, reduced.den)}`,
  };
}

export function renderTask({ data }) {
  return (
    <div>
      <p>Dane są zbiory cyfr:</p>
      <BlockMath math={`X=\\{${data.X.join(",")}\\},\\qquad Y=\\{${data.Y.join(",")}\\}.`} />
      <p>
        Losujemy cyfrę dziesiątek ze zbioru <InlineMath math="X" /> i cyfrę jedności ze zbioru{" "}
        <InlineMath math="Y" />. Oblicz prawdopodobieństwo, że otrzymana liczba będzie podzielna przez{" "}
        <InlineMath math={String(data.div)} />.
      </p>
    </div>
  );
}

export function renderInput(_task, value = {}, onChange, disabled) {
  const set = (key, next) => onChange?.({ ...value, [key]: next });
  return (
    <div style={{ display: "grid", gap: 12, maxWidth: 340 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: "grid", gap: 4 }}>
        <InlineMath math={"|\\Omega|="} />
        <FractionInput
          value={value.omega || ""}
          onChange={(next) => set("omega", next)}
          disabled={disabled}
          label={"|\\Omega|"}
          placeholder="np. 25"
        />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <InlineMath math="|A|=" />
        <FractionInput
          value={value.A || value.fav || ""}
          onChange={(next) => set("A", next)}
          disabled={disabled}
          label="|A|"
          placeholder="np. 4"
        />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <InlineMath math="P(A)=" />
        <FractionInput
          value={value.prob || ""}
          onChange={(next) => set("prob", next)}
          disabled={disabled}
          label="P(A)"
          placeholder="np. 4/25"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, value = {}) {
  const reducedProbability = probabilityResult(data.fav, data.total);
  const omega = matches(value.omega, data.total);
  const favourable = matches(value.A ?? value.fav, data.fav);
  const probability = matches(value.prob, data.fav / data.total);
  const correctParts = [omega, favourable, probability].filter(Boolean).length;
  const score = correctParts === 3 ? 2 : correctParts > 0 ? 1 : 0;
  return {
    isCorrect: score === 2,
    correct: score === 2,
    score,
    maxScore: 2,
    parts: { omega, A: favourable, probability },
    expected: {
      omega: String(data.total),
      A: String(data.fav),
      probability: `${reducedProbability.num}/${reducedProbability.den}`,
    },
  };
}

export function renderAnswer({ data }) {
  const probability = probabilityResult(data.fav, data.total);
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <InlineMath math={`|\\Omega|=${data.total}`} />
      <InlineMath math={`|A|=${data.fav}`} />
      <InlineMath math={`P(A)=${probability.latex}`} />
    </div>
  );
}

export function renderUserAnswer(value = {}) {
  const favourable = value.A ?? value.fav;
  if (![value.omega, favourable, value.prob].some((field) => rawValue(field))) {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <InlineMath math={`|\\Omega|=${answerLatex(value.omega)}`} />
      <InlineMath math={`|A|=${answerLatex(favourable)}`} />
      <InlineMath math={`P(A)=${answerLatex(value.prob)}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const probability = probabilityResult(data.fav, data.total);
  return (
    <div>
      <p><strong>Krok 1.</strong> Obliczamy liczbę wszystkich wyników.</p>
      <BlockMath math={`|\\Omega|=|X|\\cdot|Y|=${data.X.length}\\cdot${data.Y.length}=${data.total}`} />
      <p>
        <strong>Krok 2.</strong> W wierszach tabeli wybieramy cyfrę dziesiątek ze zbioru{" "}
        <InlineMath math="X" />, a w kolumnach cyfrę jedności ze zbioru <InlineMath math="Y" />.
        Zaznaczone liczby są podzielne przez <InlineMath math={String(data.div)} />.
      </p>
      <ProbabilityOutcomeTable
        rows={data.X}
        columns={data.Y}
        getCellValue={(tens, ones) => 10 * tens + ones}
        isHighlighted={(tens, ones) => (10 * tens + ones) % data.div === 0}
        ariaLabel={`Tabela liczb dwucyfrowych podzielnych przez ${data.div}`}
      />
      <p>
        Zaznaczonych liczb jest <InlineMath math={String(data.fav)} />, zatem{" "}
        <InlineMath math={`|A|=${data.fav}`} />.
      </p>
      <p><strong>Krok 3.</strong> Obliczamy prawdopodobieństwo.</p>
      <BlockMath math={`P(A)=\\frac{|A|}{|\\Omega|}=${probability.calculationLatex}`} />
    </div>
  );
}
